/**
 * CommunityDetailPage — rich community home: header, adaptive nav, tabs.
 */

import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import AppHeader from '@/components/home/AppHeader';
import { useCommunities } from './context/CommunitiesContext';
import { useModalState } from './hooks';
import { COMMUNITIES } from '@/constants/testIds';
import CommunityHeader from './CommunityHeader';
import CommunityNav from './CommunityNav';
import ChatPanel from './ChatPanel';
import PostComposer from './post/PostComposer';
import HomeTab from './tabs/HomeTab';
import DiscussionsTab from './tabs/DiscussionsTab';
import BooksTab from './tabs/BooksTab';
import EventsTab from './tabs/EventsTab';
import MembersTab from './tabs/MembersTab';
import AboutTab from './tabs/AboutTab';
import WritingTab from './tabs/WritingTab';
import EmptyState from './EmptyState';

export default function CommunityDetailPage() {
  const { communityId } = useParams();
  const { getCommunity } = useCommunities();
  const community = getCommunity(communityId);
  const [activeTab, setActiveTab] = useState('home');
  const [isChatOpen, openChat, closeChat] = useModalState(false);
  const [isComposerOpen, openComposer, closeComposer] = useModalState(false);

  if (!community) {
    return (
      <div className="relative min-h-screen bg-cream">
        <AppHeader />
        <div className="u-container py-24">
          <EmptyState type="search" heading="Community not found" subheading="It may have been renamed or removed." />
          <div className="mt-4 text-center">
            <Link to="/app/communities" className="text-sm font-semibold text-coral hover:underline">Back to Communities</Link>
          </div>
        </div>
      </div>
    );
  }

  const role = community.role ?? null;

  return (
    <div className="relative min-h-screen bg-cream" data-testid={COMMUNITIES.detailPage}>
      <AppHeader />
      <CommunityHeader community={community} role={role} onOpenChat={openChat} />
      <CommunityNav type={community.type} activeTab={activeTab} onChange={setActiveTab} />

      <main className="min-h-[50vh] pb-28 md:pb-16">
        {activeTab === 'home' && <HomeTab community={community} role={role} onTabChange={setActiveTab} />}
        {activeTab === 'discussions' && <DiscussionsTab community={community} role={role} />}
        {activeTab === 'books' && <BooksTab community={community} role={role} />}
        {activeTab === 'writing' && <WritingTab community={community} role={role} mode="writing" />}
        {activeTab === 'prompts' && <WritingTab community={community} role={role} mode="prompts" />}
        {activeTab === 'critiques' && <WritingTab community={community} role={role} mode="critiques" />}
        {activeTab === 'events' && <EventsTab community={community} role={role} />}
        {activeTab === 'members' && <MembersTab community={community} role={role} />}
        {activeTab === 'about' && <AboutTab community={community} role={role} />}
      </main>

      <button
        type="button"
        onClick={openComposer}
        aria-label="New Post"
        className="fixed bottom-20 right-6 z-30 grid h-14 w-14 place-items-center rounded-full bg-coral-fill text-[#FFF7F0] shadow-lift transition-transform hover:-translate-y-0.5 md:hidden"
      >
        <Plus className="h-6 w-6" />
      </button>

      <ChatPanel isOpen={isChatOpen} onClose={closeChat} community={community} />
      <PostComposer isOpen={isComposerOpen} onClose={closeComposer} community={community} />
    </div>
  );
}
